import { useEffect, useState, useRef, useCallback } from "react";

interface ParallaxOptions {
  speed?: number;
  direction?: "vertical" | "horizontal";
}

// Check if reduced motion is preferred
const prefersReducedMotion = () => {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

export const useParallax = (options: ParallaxOptions = {}) => {
  const { speed = 0.5, direction = "vertical" } = options;
  const [offset, setOffset] = useState(0);
  const [isInitialized, setIsInitialized] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number>();
  const lastScrollY = useRef(0);
  // Cache bounding rect to avoid repeated reflows
  const cachedRect = useRef<DOMRect | null>(null);
  const lastRectUpdate = useRef(0);

  const handleScroll = useCallback(() => {
    // Skip if reduced motion preferred
    if (prefersReducedMotion()) return;
    
    if (!ref.current) return;

    // Throttle via requestAnimationFrame
    if (rafRef.current) return;

    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = undefined;
      
      const currentScroll = window.scrollY;
      // Skip if scroll position hasn't changed significantly
      if (Math.abs(currentScroll - lastScrollY.current) < 2) return;
      lastScrollY.current = currentScroll;

      // Cache getBoundingClientRect to avoid forced reflows
      // Only update rect every 100ms or on resize
      const now = performance.now();
      if (!cachedRect.current || now - lastRectUpdate.current > 100) {
        cachedRect.current = ref.current?.getBoundingClientRect() || null;
        lastRectUpdate.current = now;
      }
      
      const rect = cachedRect.current;
      if (!rect) return;
      
      const scrolled = currentScroll;
      const elementTop = rect.top + scrolled;
      const elementHeight = rect.height;
      const windowHeight = window.innerHeight;

      // Calculate if element is in viewport
      const elementBottom = elementTop + elementHeight;
      const viewportBottom = scrolled + windowHeight;

      if (elementBottom > scrolled && elementTop < viewportBottom) {
        // Element is in viewport, calculate parallax offset
        const distance = scrolled - elementTop + windowHeight;
        const percentage = distance / (windowHeight + elementHeight);
        const movement = percentage * 100 * speed;
        setOffset(movement);
      }
    });
  }, [speed]);

  useEffect(() => {
    // Skip parallax entirely if reduced motion preferred
    if (prefersReducedMotion()) return;

    // Defer initial calculation to avoid forced reflow during render
    const initTimer = setTimeout(() => {
      setIsInitialized(true);
      handleScroll();
    }, 50);

    window.addEventListener("scroll", handleScroll, { passive: true });
    
    // Reset cached rect on resize
    const handleResize = () => {
      cachedRect.current = null;
      handleScroll();
    };
    window.addEventListener("resize", handleResize, { passive: true });

    return () => {
      clearTimeout(initTimer);
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [handleScroll]);

  const style = prefersReducedMotion() || !isInitialized ? {} : {
    transform: direction === "vertical" 
      ? `translateY(${offset}px)` 
      : `translateX(${offset}px)`,
    transition: "transform 0.1s ease-out",
  };

  return { ref, style, offset };
};
